import { CITY_PAGE_PREVIEW_COUNT } from "@noko-city/shared";

import { isCityCoverPath } from "@/lib/api-response";

/**
 * A city guide's generated live document, read on demand by the home page's city panel.
 *
 * The publisher writes `live/<slug>/data.json` plus one cover file per published post slot beside
 * each prerendered guide — the same document the guide itself is rendered from. Reading that file
 * rather than the API means the panel can only ever show a post the city's own guide already shows,
 * and a city with no published guide asks for nothing at all.
 *
 * The wire keys are this document's own (`position`/`permalink`/`imageSrcSet`), not the guide
 * payload's (`id`/`instagramUrl`), so it is decoded here rather than handed to that payload's guard:
 * a permissive read would put a broken cover or a foreign URL on the home page.
 */

/** The slots the panel shows: the city's newest few posts, in the guide's own order. */
export const LIVE_POST_COUNT = 3;

/** One slug is a URL path segment here before it is a filename, so it must be one before it is read. */
const SLUG = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * Instagram is the only outbound host a post card may name, on the two shapes the publisher writes:
 * a post permalink (reels use the canonical `/p/` shape and let Instagram redirect) and the city's
 * own profile. Anchored on both ends — a substring match would accept a foreign URL embedding one.
 */
const INSTAGRAM_TARGET =
  /^https:\/\/www\.instagram\.com\/(?:p\/[A-Za-z0-9_-]{1,64}|reel\/[A-Za-z0-9_-]{1,64}|[A-Za-z0-9._]{1,64})\/?$/;

/** One post the panel can show: a picture of ours, and where Instagram keeps the rest of it. */
export interface LivePost {
  /** The slot the guide published it in — 1 is the newest, and it names the cover file. */
  position: number;
  /** Same-origin cover path, already checked to be this city's own slot. */
  image: string;
  /** The reduced variants beside it, or null when the document offers none. */
  imageSrcSet: string | null;
  permalink: string;
}

export interface LivePosts {
  /** The city's own handle, as the guide prints it. Absent means there is nothing to follow. */
  handle: string | null;
  posts: LivePost[];
}

/** `srcset` is only usable whole: one malformed entry invalidates the list, never just itself. */
function readCoverSrcSet(value: unknown, slug: string): string | null {
  if (typeof value !== "string" || value.trim() === "") return null;
  const entries = value.split(",").map((entry) => entry.trim());
  const usable = entries.every((entry) => {
    const [url, descriptor] = entry.split(/\s+/);
    return isCityCoverPath(slug, url) && /^[1-9]\d*w$/.test(descriptor ?? "");
  });
  return usable ? value : null;
}

/** One post as the generated document carries it, before this module judges any field of it. */
interface RawLivePost {
  position?: unknown;
  image?: unknown;
  imageSrcSet?: unknown;
  permalink?: unknown;
}

/** The document's Instagram block, and the document itself: the only two shapes read here. */
interface RawLiveInstagram {
  handle?: unknown;
  posts?: unknown;
}

interface RawLiveDocument {
  instagram?: unknown;
}

/**
 * The Instagram block of one city's live document, or null when it carries nothing this panel can
 * honestly show. A post whose cover is not this city's own published file, or whose target is not an
 * Instagram one, is dropped rather than drawn; a document with no usable post at all is refused, so
 * the panel never opens onto an empty frame.
 */
export function readLivePosts(value: unknown, slug: string): LivePosts | null {
  if (typeof value !== "object" || value === null) return null;
  const { instagram } = value as RawLiveDocument;
  if (typeof instagram !== "object" || instagram === null) return null;
  const { handle, posts: rows } = instagram as RawLiveInstagram;
  if (!Array.isArray(rows)) return null;
  const known = typeof handle === "string" && handle.trim() !== "" ? handle : null;
  const posts: LivePost[] = [];
  for (const entry of rows.slice(0, CITY_PAGE_PREVIEW_COUNT)) {
    if (posts.length === LIVE_POST_COUNT) break;
    if (typeof entry !== "object" || entry === null) continue;
    const { position, permalink, image, imageSrcSet } = entry as RawLivePost;
    if (!Number.isInteger(position) || typeof permalink !== "string") continue;
    if (!INSTAGRAM_TARGET.test(permalink) || !isCityCoverPath(slug, image)) continue;
    posts.push({
      position: position as number,
      image: image as string,
      imageSrcSet: readCoverSrcSet(imageSrcSet, slug),
      permalink,
    });
  }
  return posts.length === 0 ? null : { handle: known, posts };
}

/** One request per slug per session, refusals included: a hover must not re-ask for a dead file. */
const liveRequests = new Map<string, Promise<LivePosts | null>>();

/**
 * The latest posts of one city's guide, or null when there are none to show. The document is a few
 * kilobytes of static JSON on this very origin, so the panel is a fetch of a file that already exists
 * rather than an API call the reader pays for on hover.
 */
export function loadLivePosts(slug: string): Promise<LivePosts | null> {
  if (!SLUG.test(slug)) return Promise.resolve(null);
  const cached = liveRequests.get(slug);
  if (cached) return cached;
  const request = (async (): Promise<LivePosts | null> => {
    try {
      const response = await fetch(`/live/${slug}/data.json`, {
        headers: { accept: "application/json" },
      });
      return response.ok ? readLivePosts(await response.json(), slug) : null;
    } catch {
      // An absent, unreadable or corrupt artifact is simply a city with nothing to peep at: the
      // panel is a decoration's worth of content and must never surface a failure of its own.
      return null;
    }
  })();
  liveRequests.set(slug, request);
  return request;
}
